import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, MapPin, Plus, Edit, X, Loader2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import api from '../../services/api';

const LocationManagement = () => {
    const navigate = useNavigate();
    const [locations, setLocations] = useState([]);
    const [states, setStates] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [formData, setFormData] = useState({
        stateCode: '',
        cityName: '',
        isActive: true
    });
    const [submitting, setSubmitting] = useState(false);

    const pageSize = 10;

    useEffect(() => {
        fetchLocations();
        fetchStates();
    }, [currentPage]);

    const fetchLocations = async () => {
        setLoading(true);
        try {
            const response = await api.getLocations(currentPage, pageSize);
            setLocations(response.locations);
            setTotalCount(response.totalCount);
            setTotalPages(response.totalPages);
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to fetch locations',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchStates = async () => {
        try {
            const statesData = await api.getStates();
            setStates(statesData);
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to fetch states',
                variant: 'destructive'
            });
        }
    };

    const handleAddLocation = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.createLocation({
                stateCode: formData.stateCode,
                cityName: formData.cityName
            });

            toast({
                title: 'Success',
                description: 'Location created successfully',
                variant: 'success'
            });

            setShowAddModal(false);
            setFormData({
                stateCode: states[0]?.stateCode || '',
                cityName: '',
                isActive: true
            });
            fetchLocations();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to create location',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditLocation = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.updateLocation(selectedLocation.id, {
                cityName: formData.cityName,
                isActive: formData.isActive
            });

            toast({
                title: 'Success',
                description: 'Location updated successfully',
                variant: 'success'
            });

            closeEditModal();
            fetchLocations();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update location',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const openAddModal = () => {
        setFormData({
            stateCode: states[0]?.stateCode || '',
            cityName: '',
            isActive: true
        });
        setShowAddModal(true);
    };

    const openEditModal = (location) => {
        setSelectedLocation(location);
        setFormData({
            stateCode: location.stateCode,
            cityName: location.cityName,
            isActive: location.isActive
        });
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setSelectedLocation(null);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/admin/settings')}
                        className="mb-4"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Settings
                    </Button>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shrink-0">
                                <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Location Master</h1>
                                <p className="text-gray-600 text-sm sm:text-base">Total {totalCount} locations</p>
                            </div>
                        </div>

                        <Button className="bg-green-600 hover:bg-green-700 text-white self-start sm:self-auto" onClick={openAddModal}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add Location
                        </Button>
                    </div>
                </div>

                {/* Locations Table */}
                <Card>
                    <CardHeader>
                        <CardTitle>All Locations</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center items-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-green-600" />
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[700px]">
                                        <thead className="bg-gray-50 border-b">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">City</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">State</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">State Code</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Created</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {locations.map((location) => (
                                                <tr key={location.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{location.cityName}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-600">{location.stateName}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-600">{location.stateCode}</td>
                                                    <td className="px-4 py-4">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${location.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                            }`}>
                                                            {location.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-gray-600">{formatDate(location.createdAt)}</td>
                                                    <td className="px-4 py-4">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => openEditModal(location)}
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                                    <div className="text-sm text-gray-600">
                                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} locations
                                    </div>
                                    <div className="flex space-x-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </Button>
                                        <div className="flex items-center space-x-2">
                                            {[...Array(totalPages)].map((_, idx) => {
                                                const page = idx + 1;
                                                const showPage = page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2);
                                                const showEllipsis = page === 2 && currentPage > 4;
                                                const showEndEllipsis = page === totalPages - 1 && currentPage < totalPages - 3;

                                                if (!showPage && !showEllipsis && !showEndEllipsis) return null;

                                                if (showEllipsis || showEndEllipsis) {
                                                    return <span key={page} className="px-2">...</span>;
                                                }

                                                return (
                                                    <Button
                                                        key={page}
                                                        variant={currentPage === page ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => setCurrentPage(page)}
                                                        className={currentPage === page ? "bg-green-600" : ""}
                                                    >
                                                        {page}
                                                    </Button>
                                                );
                                            })}
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Add Location Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
                    <Card className="w-full max-w-md mx-auto my-8">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Add New Location</CardTitle>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowAddModal(false)}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleAddLocation} className="space-y-4">
                                <div>
                                    <Label htmlFor="state">State *</Label>
                                    <select
                                        id="state"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                                        value={formData.stateCode}
                                        onChange={(e) => setFormData({ ...formData, stateCode: e.target.value })}
                                        required
                                    >
                                        <option value="">Select State</option>
                                        {states.map(state => (
                                            <option key={state.stateCode} value={state.stateCode}>
                                                {state.stateName} ({state.stateCode})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <Label htmlFor="city">City Name *</Label>
                                    <Input
                                        id="city"
                                        value={formData.cityName}
                                        onChange={(e) => setFormData({ ...formData, cityName: e.target.value })}
                                        required
                                        placeholder="Enter city name"
                                    />
                                </div>
                                <div className="flex justify-end space-x-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowAddModal(false)}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="bg-green-600 hover:bg-green-700"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Creating...
                                            </>
                                        ) : (
                                            'Create Location'
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Edit Location Modal */}
            {showEditModal && selectedLocation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
                    <Card className="w-full max-w-md mx-auto my-8">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Edit Location</CardTitle>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={closeEditModal}
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleEditLocation} className="space-y-4">
                                <div>
                                    <Label htmlFor="edit-state">State</Label>
                                    <Input
                                        id="edit-state"
                                        value={`${selectedLocation.stateName} (${selectedLocation.stateCode})`}
                                        disabled
                                        className="bg-gray-100"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">State cannot be changed</p>
                                </div>
                                <div>
                                    <Label htmlFor="edit-city">City Name *</Label>
                                    <Input
                                        id="edit-city"
                                        value={formData.cityName}
                                        onChange={(e) => setFormData({ ...formData, cityName: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="edit-active"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        className="rounded"
                                    />
                                    <Label htmlFor="edit-active" className="cursor-pointer">Active</Label>
                                </div>
                                <div className="flex justify-end space-x-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={closeEditModal}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="bg-green-600 hover:bg-green-700"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Updating...
                                            </>
                                        ) : (
                                            'Update Location'
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}
            <Toaster />
        </div>
    );
};

export default LocationManagement;
